import React, { Ref } from 'react'

import classNames from 'classnames'

import { CommonVerifiedIcon, CommonViewCountIcon } from '@local/assets/icons'

import Clickable from '@web/components/Clickable'
import Image from '@web/components/Image'
import ProfileThumbnail from '@web/components/ProfileThumbnail'
import { gql } from '@web/gql'
import { ThemelistCard_ThemelistFragment } from '@web/gql/graphql'
import { useGoTo } from '@web/hooks/useGoTo'
import routes from '@web/libs/constants/routes'
import { getThumbnail } from '@web/libs/media'
import photoInfra from '@web/libs/media/photoInfra'
import { truncateNumber } from '@web/libs/utils'

import ReviewGroupThumbnails from './ReviewGroupThumbnails/ReviewGroupThumbnails'
import styles from './ThemelistCard.module.scss'

export enum ViewType {
  REVIEW_TAB = 'reviewTab',
  FEED = 'feed',
}

interface Props {
  viewType: ViewType
  themelist: ThemelistCard_ThemelistFragment
  clickCode: string
}

const ThemelistCardComponent = ({ viewType, themelist, clickCode }: Props, ref: Ref<HTMLLIElement>) => {
  const { goTo } = useGoTo()
  return (
    <li
      className={classNames(styles.list_item, {
        [styles.type_review_tab]: viewType === ViewType.REVIEW_TAB,
      })}
      ref={ref}
    >
      <Clickable
        className={classNames(styles.list_item_link, styles[`color${themelist.colorNumber}`])}
        onClick={() => goTo(routes.THEME_END, { params: { themeId: themelist.id } })}
        nClickCode={clickCode}
      >
        <div className={styles.top_thumb_area}>
          {viewType === ViewType.FEED && (
            <div className={styles.profile_area}>
              <div className={styles.profile_inner}>
                <div className={styles.profile_thumb_wrapper}>
                  <ProfileThumbnail
                    size={30}
                    forceLightMode
                    thumbnailUrl={themelist.user.profile?.originImageUrl}
                    isFollowing={themelist.user?.isFriend}
                  />
                </div>
                <div className={styles.name_area}>
                  <div className={styles.name_inner}>
                    {themelist.user?.profile.nickname}
                    {themelist.user.isThemeContentProvider && (
                      <CommonVerifiedIcon className={styles.icon_verified} aria-hidden="true" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={styles.top_thumb_area_inner}>
            <div className="place_thumb">
              {themelist.coverMediaItem && (
                <Image
                  className={styles.img}
                  src={getThumbnail(themelist.coverMediaItem) ?? ''}
                  width="100%"
                  height="100%"
                  alt=""
                  thumbnailType={photoInfra.THUMBNAIL_TYPES.myplace.m385_385_70_sharpen}
                />
              )}
              {viewType === ViewType.REVIEW_TAB && !themelist?.isPublic && <span className={styles.label}>비공개</span>}
            </div>
          </div>
        </div>
        <div className={styles.inner}>
          <div className={styles.info_area}>
            <div className={styles.tit_area}>
              <div className={styles.tit_inner}>{themelist.title}</div>
            </div>
            <div className={styles.etc_area}>
              <span className={styles.item}>장소 {themelist.statistic.placesCount}</span>
              <span className={styles.item}>
                <CommonViewCountIcon className={styles.icon} aria-hidden="true" />
                <span className="place_blind">조회</span>
                {truncateNumber(themelist.statistic?.viewCount)}
              </span>
            </div>
          </div>
          <ReviewGroupThumbnails
            reviewGroupsCount={themelist.statistic.reviewGroupsCount}
            reviewGroups={themelist.reviewGroups}
            viewType={viewType}
          />
        </div>
      </Clickable>
    </li>
  )
}

const ThemelistCard = {
  fragments: {
    themelist: gql(`
      fragment ThemelistCard_Themelist on Themelist {
        id
        isPublic
        colorNumber
        user {
          id
          idno
          profile {
            nickname
            originImageUrl
          }
          isFriend
          isThemeContentProvider
        }
        statistic {
          viewCount
          placesCount
          reviewGroupsCount
        }
        coverMediaItem {
          id
          type
          ... on VideoItem {
            thumbnail
          }
          ... on ImageItem {
            url
          }
        }
        title
        reviewGroups(page: 1, limit: 3) {
          id
          ...ReviewGroupThumbnails_ReviewGroup
        }
      }
    `),
  },
  component: React.forwardRef(ThemelistCardComponent),
}

export default ThemelistCard
